import React, { useState } from 'react';
import './Contact.css';

const CONTACT_EMAIL = 'info@softnovasolutions.com';
const CONTACT_SITE = 'softnovasolutions.com';
const OFFICE_ADDRESS = 'طريق الملك عبدالله, King Faisal Dt., Riyadh 14232';
const MAP_QUERY = encodeURIComponent('King Abdullah Road, King Faisal District, Riyadh 14232');
const MAP_SRC = `https://maps.google.com/maps?q=${MAP_QUERY}&z=16&output=embed`;
const MAP_LINK = `https://maps.google.com/?q=${MAP_QUERY}`;

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M4 7l8 6 8-6" fill="none" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const WebIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M4 12h16M12 4c2.4 2.6 3.6 5.3 3.6 8s-1.2 5.4-3.6 8c-2.4-2.6-3.6-5.3-3.6-8s1.2-5.4 3.6-8z" fill="none" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7.5V12l3.2 2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="12" cy="10" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const buildEmailBody = () => (
    [
      'New inquiry from the Softnova website',
      '',
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone || 'Not provided'}`,
      `Subject: ${formData.subject}`,
      '',
      'Project / query:',
      formData.message,
    ].join('\n')
  );

  const sendViaFormSubmit = async () => {
    const response = await fetch(`https://formsubmit.co/ajax/${CONTACT_EMAIL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || 'Not provided',
        subject: formData.subject,
        message: formData.message,
        _replyto: formData.email,
        _subject: `Softnova website inquiry: ${formData.subject}`,
        _template: 'table',
        _captcha: 'false',
      }),
    });

    if (!response.ok) {
      throw new Error('FormSubmit request failed');
    }

    return response.json();
  };

  const sendViaApi = async () => {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        website: formData.website,
      }),
    });

    if (!response.ok) {
      throw new Error('API request failed');
    }

    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website) return;

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      try {
        await sendViaFormSubmit();
      } catch (primaryError) {
        await sendViaApi();
      }

      setStatus({
        type: 'success',
        message: 'Thank you. Your message has been sent. We will reply to your email shortly.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        website: '',
      });
    } catch (error) {
      const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Website inquiry: ${formData.subject}`)}&body=${encodeURIComponent(buildEmailBody())}`;
      setStatus({
        type: 'error',
        message: `We could not send automatically. Please email ${CONTACT_EMAIL} or use the direct link below.`,
      });
      window.location.href = mailto;
    } finally {
      setIsSubmitting(false);
    }
  };

  const details = [
    {
      icon: <PinIcon />,
      title: 'Address',
      value: OFFICE_ADDRESS,
      href: MAP_LINK,
      note: 'King Faisal District, Riyadh',
    },
    {
      icon: <EmailIcon />,
      title: 'Email',
      value: CONTACT_EMAIL,
      href: `mailto:${CONTACT_EMAIL}`,
      note: 'Typical reply within one business day',
    },
    {
      icon: <WebIcon />,
      title: 'Website',
      value: CONTACT_SITE,
      href: `https://${CONTACT_SITE}`,
      note: 'softnovasolutions.com',
    },
    {
      icon: <ClockIcon />,
      title: 'Working hours',
      value: 'Monday to Friday, 9:00 to 18:00',
      href: null,
      note: 'Weekend messages are answered the next business day',
    },
  ];

  return (
    <main className="contact-page">
      <section className="contact-hero page-hero section">
        <div className="container">
          <p className="eyebrow">Contact</p>
          <h1 className="section-title">Start a project conversation</h1>
          <p className="section-subtitle">
            Share a short brief. We receive every inquiry at {CONTACT_EMAIL}.
          </p>
        </div>
      </section>

      <section className="contact-content section">
        <div className="container">
          <div className="contact-layout">
            <aside className="contact-aside">
              <h2>Get in touch</h2>
              <p>
                Use the form or write us directly. Include platforms, timeline, and any
                systems already in place so we can reply with a clear next step.
              </p>
              <ul className="contact-detail-list">
                {details.map((item) => (
                  <li key={item.title} className="contact-detail">
                    <span className="contact-detail-icon">{item.icon}</span>
                    <div>
                      <h3>{item.title}</h3>
                      {item.href ? (
                        <a href={item.href}>{item.value}</a>
                      ) : (
                        <p className="contact-detail-value">{item.value}</p>
                      )}
                      <span>{item.note}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </aside>

            <div className="contact-form-card">
              <h2>Send a request</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="honeypot" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Include country code"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject">I need help with *</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a topic</option>
                      <option value="Web Application (.NET)">Web application (.NET)</option>
                      <option value="Web Application (PHP / Laravel)">Web application (PHP / Laravel)</option>
                      <option value="Microservices / APIs">Microservices / APIs</option>
                      <option value="Mobile App (iOS / Android)">Mobile app (iOS / Android)</option>
                      <option value="Digital Marketing & SEO">Digital marketing & SEO</option>
                      <option value="Combined Product + Growth">Combined product + growth</option>
                      <option value="General Inquiry">General inquiry</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Describe your project *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="What you want to build, platforms, timeline, and any current systems."
                  />
                </div>

                {status.message && (
                  <div className={`form-status ${status.type}`}>
                    {status.message}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary contact-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-map-section">
        <div className="container">
          <div className="contact-map-head">
            <div>
              <p className="eyebrow">Office</p>
              <h2>Visit us in Riyadh</h2>
              <p>{OFFICE_ADDRESS}</p>
            </div>
            <a className="contact-map-link" href={MAP_LINK} target="_blank" rel="noreferrer">
              Open map
            </a>
          </div>
          <div className="contact-map-frame">
            <iframe
              title="Softnova Solutions map"
              src={MAP_SRC}
              width="600"
              height="120"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
